import debounce from '@/mixins/debounce';
import {mapGetters, mapMutations} from 'vuex';
import errorHandler from '@/mixins/errorHandler';

export default {
    mixins: [errorHandler, debounce],
    data() {
        return {
            tagsList: [],
            messageTagsList: [],
            chatTags: [],
            messages: [],
            comments: [],
            pinnedMessages: [],
            highlightMessages: [],
            reminderMessages: [],
            countReminders: 0,
            unBlurredMessage: null,
            taggingMessage: null,
            editingCommentId: null,
            currentRoute: null,
            keySearch: '',
            chatPage: 1,
            chatSize: 10,
            hasMoreMessages: false,
            remindAt: null,
        };
    },
    computed: {
        ...mapGetters('messagesTagging', {
            chatData: 'getChatData',
            isTagging: 'isTagging',
        })
    },
    mounted() {
        this.currentRoute = this.$route.path.substring(this.$route.path.lastIndexOf('/') + 1);
    },
    methods: {
        ...mapMutations('messagesTagging', {
            setLoadingState: 'setLoadingState',
        }),

        // ---- TAGGING ----
        getListTags() {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    const res = await this.$api.messagesTagging.getListTags(this.chatData.caseId);
                    this.tagsList = res.data.body;
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        addNewCategory(categoryName) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.addNewCategory(this.chatData.caseId, {category: categoryName});
                    await this.getListTags();
                    await this.getMessageTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        editCategory({categoryId, categoryName}) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.editCategory(
                        this.chatData.caseId,
                        categoryId,
                        {category: categoryName}
                    );
                    await this.getListTags();
                    await this.getMessageTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        removeCategory(categoryId) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.removeCategory(this.chatData.caseId, categoryId);
                    await this.getListTags();
                    await this.getMessageTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                () => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    throw new Error('');
                }
            );
        },
        changeCategoryOrder(categoryId, orderIndex) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.changeCategoryOrder(this.chatData.caseId, {categoryId, orderIndex});
                    await this.getMessageTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        addNewTag({categoryId, newTagName}) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.addNewTag(this.chatData.caseId, categoryId, {
                        tag: newTagName,
                    });
                    await this.getListTags();
                    await this.getMessageTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        editTag({tagId, tagName}) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.editTag(this.chatData.caseId, tagId, {tag: tagName});
                    await this.getListTags();
                    await this.getMessageTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        async removeTag(tagId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.messagesTagging.removeTag(tagId, this.chatData.caseId);
                    await this.getListTags();
                    await this.getMessageTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                }, () => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    throw new Error('');
                }
            );
        },
        changeTagOrder({tagId, categoryId, orderIndex}) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.changeTagOrder(this.chatData.caseId, {
                        id: tagId,
                        categoryId,
                        orderIndex,
                    });
                    await this.getMessageTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        getMessageTags() {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    const res = await this.$api.messagesTagging.getMessageListTags(
                        this.chatData.caseId,
                        this.chatData.taggingMessageId
                    );
                    this.messageTagsList = (res.data.body.categories || []).map(cat => ({
                        ...cat,
                        tags: (cat.tags || []).map(tag => ({
                            ...tag,
                            isActive: tag.isSelected,
                        })),
                    }));
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        getListTagsByChatId() {
            this.$load(async () => {
                    const res = await this.$api.messagesTagging.getListTagsByChatId(
                        this.chatData.caseId,
                        this.chatData.chatId
                    );
                    this.chatTags = (res.data.body || []).map(category => ({
                        ...category,
                        tags: (category.tags || []).map(tag => ({
                            ...tag,
                            isActive: true,
                        })),
                    }));
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                });
        },

        // ---- MESSAGES LOADING ----
        async getChatMessages() {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const res = await this.$api.messagesTagging.getChatMessages(this.chatData.caseId, this.chatData.chatId, {
                        page: this.chatPage,
                        size: this.chatSize,
                        // keySearch: this.keySearch,
                        keySearch: '',
                    });
                    await this.updateNewMessages(res);
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        async getListMessagesByTags() {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const activeTags = this.chatTags
                        .flatMap(cat => cat.tags.filter(t => t.isActive).map(t => t.tag))
                        .join(',');
                    const res = await this.$api.messagesTagging.getMessagesByTags(
                        this.chatData.caseId,
                        this.chatData.chatId,
                        {
                            keySearch: this.keySearch,
                            tags: activeTags,
                            page: this.chatPage,
                            size: this.chatSize,
                        });
                    await this.updateNewMessages(res);
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        // ---- MESSAGE DETAILS ----
        getMessageById(messageId, updateExistingMessages = false) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    const id = messageId || this.chatData.taggingMessageId;
                    const res = await this.$api.messagesTagging.getMessageById(
                        this.chatData.caseId,
                        id,
                        this.chatData.chatId
                    );
                    this.taggingMessage = res.data.body;
                    await this.getMessageTags();
                    await this.getCommentsByMessageId();
                    if (updateExistingMessages && this.currentRoute !== 'comments') {
                        this.updateExistingMessages(res.data.body, this.messages);
                    }
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        async addTagToMessage(tagId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.messagesTagging.addTagToMessage(
                        this.chatData.caseId,
                        this.chatData.taggingMessageId,
                        tagId
                    );
                    if (this.currentRoute.includes('reminder')) {
                        await this.getReminderMessages();
                    } else {
                        await this.getMessageById();
                    }
                    await this.getMessageTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        async removeTagFromMessage(tagId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.messagesTagging.removeTagFromMessage(
                        this.chatData.caseId,
                        this.chatData.taggingMessageId,
                        tagId
                    );
                    if (this.currentRoute.includes('reminder')) {
                        await this.getReminderMessages();
                    } else {
                        await this.getMessageById();
                    }
                    await this.getMessageTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        async blurMessage() {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.messagesTagging.blurMessage(this.chatData.caseId, this.chatData.taggingMessageId);
                    await this.getMessageById();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        async unblurMessage() {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.messagesTagging.unblurMessage(this.chatData.caseId, this.chatData.taggingMessageId);
                    await this.getMessageById();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        showBlurred() {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    const res = await this.$api.messagesTagging.showBlurredMessage(
                        this.chatData.caseId,
                        this.chatData.taggingMessageId
                    );
                    this.unBlurredMessage = res.data.body.message;
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        clearUnBlurredMessage() {
            this.unBlurredMessage = null;
        },

        // ---- COMMENTS ----
        async getListCommentsByChatId() {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const res = await this.$api.messagesTagging.getListCommentsByChatId(
                        this.chatData.caseId,
                        this.chatData.chatId,
                        {searchKey: this.keySearch, page: this.chatPage, size: this.chatSize}
                    );
                    await this.updateNewMessages(res);
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        getCommentsByMessageId() {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    const res = await this.$api.messagesTagging.getCommentsByMessageId(
                        this.chatData.caseId,
                        this.chatData.taggingMessageId
                    );
                    this.comments = res.data.body;
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        createCommentForMessage(text) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.createCommentForMessage(
                        this.chatData.caseId,
                        this.chatData.taggingMessageId,
                        {text}
                    );
                    await this.getCommentsByMessageId();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        changeComment(commentId, newText) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.updateComment(
                        this.chatData.caseId,
                        commentId,
                        {text: newText}
                    );
                    this.getCommentsByMessageId();
                    if (this.currentRoute === 'comments') {
                        await this.getListCommentsByChatId();
                    }
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        deleteComment(commentId) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.deleteComment(this.chatData.caseId, commentId);
                    await this.getCommentsByMessageId();
                    if (this.currentRoute === 'comments') {
                        await this.getListCommentsByChatId();
                    }
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        // ---- PINNED ----
        pinMessage() {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.pinMessage(
                        this.chatData.caseId,
                        this.chatData.chatId,
                        this.chatData.taggingMessageId
                    );
                    await this.getMessageById();
                    this.debounce(() => {
                        if (['pinned', 'edit'].includes(this.currentRoute)) {
                            if (!this.messages.find(m => m.id === this.chatData.taggingMessageId)) {
                                this.messages.push(this.messages.find(m => m.id === this.chatData.taggingMessageId));
                                this.messages.sort((a, b) => b.id - a.id);
                            }
                        }
                        this.debounce(() => this.setLoadingState(false), 1000);
                    }, 500);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        unpinMessage() {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.unpinMessage(
                        this.chatData.caseId,
                        this.chatData.chatId,
                        this.chatData.taggingMessageId
                    );
                    if (this.currentRoute === 'pinned') {
                        this.messages = this.messages.filter(m => m.id !== this.chatData.taggingMessageId);
                    }
                    await this.getMessageById();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        // ---- HIGHLIGHT ----
        async getListHighlightMessages() {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const res = await this.$api.messagesTagging.getListHighlightMessages(
                        this.chatData.caseId,
                        this.chatData.chatId,
                        {keySearch: this.keySearch, page: this.chatPage, size: this.chatSize}
                    );
                    await this.updateNewMessages(res);
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        async createHighlightForMessage(color) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const hex = color.replace('#', '');
                    await this.$api.messagesTagging.createHighlightForMessage(
                        this.chatData.caseId,
                        this.chatData.taggingMessageId,
                        hex
                    );
                    await this.getMessageById();
                    // this.debounce(() => {
                    //     if (['highlight', 'edit'].includes(this.currentRoute)) {
                    //         if (!this.messages.find(m => m.id === this.chatData.taggingMessageId)) {
                    //             this.messages.push(this.messages.find(m => m.id === this.chatData.taggingMessageId));
                    //             this.messages.sort((a, b) => b.id - a.id);
                    //         }
                    //     }
                    // }, 500);
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        deleteHighlightForMessage() {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.deleteHighlightForMessage(
                        this.chatData.caseId,
                        this.chatData.taggingMessageId
                    );
                    await this.getMessageById();
                    this.debounce(() => {
                        if (this.currentRoute === 'highlight') {
                            this.messages = this.messages.filter(m => m.id !== this.chatData.taggingMessageId);
                        }
                        this.debounce(() => this.setLoadingState(false), 1000);
                    }, 500);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        // ---- REMINDERS ----
        async getReminderMessages() {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const res = await this.$api.messagesTagging.getReminderMessages(
                        this.chatData.caseId,
                        this.chatData.chatId,
                        {keySearch: this.keySearch, page: this.chatPage, size: this.chatSize}
                    );
                    await this.updateNewMessages(res);
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        addReminder(reminderTime) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.addReminder({
                        remindAt: reminderTime,
                        caseId: this.chatData.caseId,
                        chatId: this.chatData.chatId,
                        messageId: this.chatData.taggingMessageId,
                    });
                    this.debounce(() => {
                        this.countActiveRemindersByChatId();
                        this.debounce(() => this.setLoadingState(false), 1000);
                    }, 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        editReminder({reminderId, reminderTime}) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.editReminder(
                        this.chatData.caseId,
                        reminderId,
                        {remindAt: reminderTime}
                    );
                    await this.getReminderMessages();
                    // this.messages = this.messages.map(m => {
                    //     if (m.reminder.id === reminderId) {
                    //         m.reminder.remindIn = this.remindAt;
                    //     }
                    //     return m;
                    // });
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        deleteReminder(reminderId) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.messagesTagging.deleteReminder(this.chatData.caseId, reminderId);
                    if (this.currentRoute === 'reminders') {
                        this.messages = this.messages.filter(m => m.reminder.id !== reminderId);
                    }
                    this.debounce(() => {
                        this.countActiveRemindersByChatId();
                        this.debounce(() => this.setLoadingState(false), 1000);
                    }, 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
        markAsCompleteReminder(reminderId) {
            this.$load(
                async () => {
                    await this.$api.messagesTagging.markAsCompleteReminder(this.chatData.caseId, reminderId);
                    if (this.currentRoute === 'reminders') {
                        this.messages = this.messages.filter(m => m.reminder.id !== reminderId);
                    }
                    this.debounce(() => this.countActiveRemindersByChatId(), 1000);
                },
                error => this.handleResponseError(error)
            );
        },
        countActiveRemindersByChatId() {
            this.$load(
                async () => {
                    const res = await this.$api.messagesTagging.countActiveReminders(
                        this.chatData.caseId,
                        this.chatData.chatId
                    );
                    this.countReminders = res.data.body.count;
                },
                error => this.handleResponseError(error)
            );
        },

        // MESSAGES UTILS
        filterNewMessages(newMessages, oldMessages) {
            const existingMap = new Map(oldMessages.map(msg => [msg.id, msg]));
            const merged = newMessages.map(nm => {
                const exist = existingMap.get(nm.id);
                return exist ? {...exist, ...nm} : nm;
            });
            const newIds = new Set(newMessages.map(m => m.id));
            const remaining = oldMessages.filter(m => !newIds.has(m.id));
            return [...remaining, ...merged];
        },

        updateExistingMessages(message) {
            const index = this.messages.findIndex(item => item.id === message.id);
            if (index !== -1) {
                const newMessages = [...this.messages];
                newMessages[index] = message;
                this.messages = newMessages;
            } else {
                this.messages.push(message);
                this.messages.sort((a, b) => b.id - a.id);
            }

            this.$nextTick(() => {
                this.infiniteId += 1;
            });
        },

        updateNewMessages(res) {
            const body = res.data.body;
            const content = body.content;
            let final;
            if (this.chatPage === 1) {
                final = content;
            } else {
                final = this.filterNewMessages(content, this.messages);
            }
            this.messages = final;
            if (body.last) {
                this.hasMoreMessages = false;
            } else {
                this.hasMoreMessages = true;
                this.chatPage += 1;
            }
        },
    },
};
